// Construcción centralizada de URLs internas. Toda navegación interna debe usar
// estos builders para que las formas de URL vivan en un único sitio. Las rutas
// cuelgan de un segmento real `entradas/` (carpeta de verdad, no route group):
// los route groups `(general-web)`/`(canales)` no añaden segmento, pero
// `entradas/` sí, así que todo builder antepone `BASE` — según
// `specs/MIGRATION-PLAN.md` §3.2.
//
// Toda URL interna acaba en `/` (ver `trailingSlash: true` en `next.config.ts`);
// los builders lo garantizan aquí para que la navegación interna use ya la forma
// canónica, sin depender de la redirección 308 del framework.

import { ALL_LOCATIONS } from '@/app/lib/constants'

const BASE = '/entradas'

/** Codifica un segmento dinámico (slug, location, canal…) para no romper la URL. */
function seg(value: string): string {
  return encodeURIComponent(value)
}

/** Une segmentos ya codificados en un path absoluto bajo `entradas/`, descartando vacíos. */
function path(...segments: Array<string | number | undefined>): string {
  const joined = segments
    .filter((s): s is string | number => s !== undefined && s !== '')
    .map((s) => seg(String(s)))
    .join('/')
  return joined ? `${BASE}/${joined}/` : `${BASE}/`
}

const EXTERNAL = /^(https?:)?\/\/|^(#|mailto:|tel:)/

/** Normaliza un path interno a forma absoluta bajo `entradas/`, acabada en `/`. Externos: sin tocar. */
export function route(input: string): string {
  if (!input || input === '/') return `${BASE}/`
  if (EXTERNAL.test(input)) return input
  const normalized = input.startsWith('/') ? input : `/${input}`
  const withBase = `${BASE}${normalized}`
  return withBase.endsWith('/') ? withBase : `${withBase}/`
}

/** Mapa canónico de URLs de ECI Entradas. */
export const routes = {
  /** Home → `/entradas/`. */
  home: (): string => `${BASE}/`,

  /** Home localizada → `/entradas/{location}/`. */
  homeByLocation: (location: string): string => path(location),

  /**
   * Agenda → `/entradas/agenda/` (sin localización, ruta de contenido real — no
   * redirige, a diferencia de `loMasVendido`) o `/entradas/agenda/{location}/` con
   * una provincia concreta. Género/subgénero/día viajan como query params, añadidos
   * por el llamador (ver `specs/015-agenda-listado`).
   */
  agenda: (location: string = ALL_LOCATIONS): string =>
    location === ALL_LOCATIONS ? path('agenda') : path('agenda', location),

  /**
   * Recintos → `/entradas/recintos/` sin localización o
   * `/entradas/recintos/{location}/` con una provincia concreta. Igual que
   * `agenda`: `ALL_LOCATIONS` omite el segmento — la ruta bare renderiza el
   * mismo contenido sin redirect (el proxy normaliza `/todos/` hacia aquí).
   */
  recintos: (location: string = ALL_LOCATIONS): string =>
    location === ALL_LOCATIONS ? path('recintos') : path('recintos', location),

  /** Detalle de recinto → `/entradas/recintos/{location}/{url}/`. */
  recinto: (location: string, url: string): string => path('recintos', location, url),

  /** Índice de artistas → `/entradas/artistas/`. */
  artistas: (): string => path('artistas'),

  /** Artista → `/entradas/artistas/{slug}/`. */
  artist: (slug: string): string => path('artistas', slug),

  /**
   * Promociones → `/entradas/promociones/` sin localización o
   * `/entradas/promociones/{location}/` con una provincia concreta. Igual
   * que `agenda`: `ALL_LOCATIONS` omite el segmento — la ruta bare
   * renderiza el mismo contenido sin redirect (el proxy normaliza
   * `/todos/` hacia aquí).
   */
  promociones: (location: string = ALL_LOCATIONS): string =>
    location === ALL_LOCATIONS ? path('promociones') : path('promociones', location),

  /**
   * Lo más vendido → `/entradas/lo-mas-vendido/` sin localización o
   * `/entradas/lo-mas-vendido/{location}/` con una provincia concreta.
   * Igual que `agenda`: `ALL_LOCATIONS` omite el segmento — la ruta bare
   * renderiza el mismo contenido sin redirect (el proxy normaliza
   * `/todos/` hacia aquí).
   */
  loMasVendido: (location: string = ALL_LOCATIONS): string =>
    location === ALL_LOCATIONS ? path('lo-mas-vendido') : path('lo-mas-vendido', location),

  /** Post venta → `/entradas/postsale/`. */
  postsale: (): string => path('postsale'),

  /** Página de búsqueda → `/entradas/search/?q=<query>`. */
  search: (query?: string): string =>
    query ? `${path('search')}?q=${encodeURIComponent(query)}` : path('search'),

  /** Detalle de evento (PDP) → `/entradas/{categoria}/{evento}/`. Las promos comparten forma. */
  event: (categoria: string, evento: string): string => path(categoria, evento),

  /** Listado (PLP) → `/entradas/{categoria}/{location}/{pagina}/`. */
  plp: (categoria: string, location: string, pagina: string): string =>
    path(categoria, location, pagina),

  /**
   * PLP de subcategoría → `/entradas/{categoria}/{location}/{subcategoria}/`.
   * Seleccionar una provincia cambia el segmento `location` (por defecto `todos`),
   * mientras que el slug enviado a `/data` sigue siendo `categoria/subcategoria`
   * (ver `specs/021-subcategoria-plp`).
   */
  subcategoria: (categoria: string, location: string, subcategoria: string): string =>
    path(categoria, location, subcategoria),

  /** Landing de un slug (categoría o landing de evento/artista) → `/entradas/{slug}/`. */
  page: (slug: string): string => path(slug),

  /** Home de canal → `/entradas/channels/{channel}/`. */
  channel: (channel: string): string => path('channels', channel),

  /** Performance de canal → `/entradas/channels/{channel}/performance/{evento}/`. */
  channelPerformance: (channel: string, evento: string): string =>
    path('channels', channel, 'performance', evento),

  /** Postventa de canal → `/entradas/channels/{channel}/postsales/`. */
  channelPostsales: (channel: string): string => path('channels', channel, 'postsales'),

  /** Ayuda (preguntas frecuentes) de canal → `/entradas/channels/{channel}/faqs/`. */
  channelFaqs: (channel: string): string => path('channels', channel, 'faqs'),
} as const

export type Routes = typeof routes
